import type { QueriesOptions } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

import { getDataFromResponse } from "~/api/axiosUtils";
import {
	getTimeTracking,
	getUsersTimeTrackings,
	getUsersUntrackedDaysByMonth,
	indexProjectsTimeTrackings,
} from "~/modules/timeTracking/api/timeTracking/timeTrackingApiDispatchers.ts";
import type { GetUsersTimeTrackingsFilterType } from "~/modules/timeTracking/api/timeTracking/timeTrackingTypes.ts";

export const TIME_TRACKING_BASE_QUERY_KEY = "timeTracking";

export const TIME_TRACKINGS_BY_USER_QUERY_KEY = (userId: string,
	filter?: GetUsersTimeTrackingsFilterType) => [TIME_TRACKING_BASE_QUERY_KEY, { userId }, filter];
export const TIME_TRACKING_QUERY_KEY = (timeTrackingId: string) => [TIME_TRACKING_BASE_QUERY_KEY, { timeTrackingId }];

export const PROJECTS_TIME_TRACKINGS_QUERY_KEY = (projectId: string) => [TIME_TRACKING_BASE_QUERY_KEY, { projectId }];
export const useUsersTimeTrackings = ({ userId, filter, enabled = true }: {
	userId: string,
	filter?: GetUsersTimeTrackingsFilterType
	enabled?: boolean
}) => useQuery({
	queryKey: TIME_TRACKINGS_BY_USER_QUERY_KEY(userId, filter),
	queryFn: () => getUsersTimeTrackings(userId, filter).then(getDataFromResponse),
	enabled,
});

export const useProjectsTimeTrackings = (projectId: string) => useQuery({
	queryKey: PROJECTS_TIME_TRACKINGS_QUERY_KEY(projectId),
	queryFn: () => indexProjectsTimeTrackings(projectId).then(getDataFromResponse),
	enabled: !!projectId,
});

export const USERS_UNTRACKED_DAYS_QUERY_KEY = (startOfMonth: string) => ["usersUntrackedDays", startOfMonth];

export const useUsersUntrackedDays = (userId: string, startOfMonth: string, options?: QueriesOptions<any>) => useQuery({
	queryKey: USERS_UNTRACKED_DAYS_QUERY_KEY(startOfMonth),
	queryFn: () => getUsersUntrackedDaysByMonth(userId, startOfMonth).then(getDataFromResponse),
	...options,
});

export const useTimeTracking = ({ userId, timeTrackingId, options }: {
	userId: string, timeTrackingId: string, options?: QueriesOptions<any>
}) => useQuery({
	queryKey: TIME_TRACKING_QUERY_KEY(timeTrackingId),
	queryFn: () => getTimeTracking(userId, timeTrackingId).then(getDataFromResponse),
	...options,
});